/**
 * 列型変換ユーティリティ
 *
 * 複数列の型変換を行うためのSQL生成と関連機能
 */

import { DataType } from '../../types/core.types';
import { ColumnId, ColumnName } from '../../types';
import { toDuckDBType } from './duckdb-type-mapping';
import { ROW_ID_COLUMN_NAME } from '../../types/system-columns';

/**
 * 列名を二重引用符でエスケープする
 * DuckDBでは特殊文字を含む列名は二重引用符で囲む必要がある
 */
function escapeColumnName(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

/**
 * 変換失敗時の処理方法
 */
export type OnErrorMode = 'null' | 'exclude' | 'fail';

/**
 * 列の型変換設定
 */
export interface ColumnConversion {
  columnId: ColumnId;
  columnName: ColumnName;
  fromType: DataType;
  toType: DataType;
  onError: OnErrorMode;
  enabled: boolean;
}

/**
 * SQL生成結果
 */
export interface ConversionSQLResult {
  sql: string;
  needsExcludeQuery: boolean;  // Exclude対象行を特定するクエリが必要か
  excludeQueryColumns: string[];  // Exclude判定が必要な列名
}

/**
 * 列型変換のためのSQLを生成
 *
 * @param sourceTableName - 元テーブル名（DuckDBに登録されている名前）
 * @param columns - データセットの全列
 * @param conversions - 変換設定の配列
 * @returns 生成されたSQL文字列と関連情報
 */
export function generateConversionSQL(
  sourceTableName: string,
  columns: Array<{ id: ColumnId; name: string; type: DataType }>,
  conversions: ColumnConversion[]
): ConversionSQLResult {
  const conversionMap = new Map(
    conversions
      .filter(c => c.enabled)
      .map(c => [c.columnId, c])
  );

  const selectClauses: string[] = [];
  const excludeQueryColumns: string[] = [];

  for (const column of columns) {
    const conversion = conversionMap.get(column.id);
    const escapedName = escapeColumnName(column.name);

    // 変換設定がない、または型が同じ場合はそのまま
    if (!conversion || conversion.fromType === conversion.toType) {
      selectClauses.push(escapedName);
      continue;
    }

    const duckdbType = toDuckDBType(conversion.toType);

    switch (conversion.onError) {
      case 'null':
        // TRY_CAST: 失敗時にNULL
        selectClauses.push(
          `TRY_CAST(${escapedName} AS ${duckdbType}) AS ${escapedName}`
        );
        break;

      case 'exclude':
        // 通常のTRY_CASTを使用（後でexclude対象を特定する）
        selectClauses.push(
          `TRY_CAST(${escapedName} AS ${duckdbType}) AS ${escapedName}`
        );
        excludeQueryColumns.push(column.name);
        break;

      case 'fail':
        // CAST: 失敗時にエラー
        selectClauses.push(
          `CAST(${escapedName} AS ${duckdbType}) AS ${escapedName}`
        );
        break;
    }
  }

  const sql = `SELECT ${selectClauses.join(', ')} FROM ${escapeColumnName(sourceTableName)}`;

  return {
    sql,
    needsExcludeQuery: excludeQueryColumns.length > 0,
    excludeQueryColumns
  };
}

/**
 * 変換失敗行を特定するSQLを生成
 *
 * Exclude mode指定された列で変換失敗した行のRow #を取得する
 *
 * 注意: Row #列は別名（source_row_id）で取得する。
 * これは、arrowToOperationResultがRow #列を自動的に削除・再生成するため、
 * 元のRow #値を保持するために別名を使用する必要があるため。
 *
 * @param sourceTableName - 元テーブル名
 * @param excludeColumns - Exclude判定対象の列情報
 * @returns 失敗行のRow #取得SQL
 */
export function generateExcludeRowsSQL(
  sourceTableName: string,
  excludeColumns: Array<{ name: string; fromType: DataType; toType: DataType }>
): string {
  const conditions = excludeColumns.map(col => {
    const duckdbType = toDuckDBType(col.toType);
    const escapedName = escapeColumnName(col.name);
    // 元の値がNULLでなく、かつ変換結果がNULLの場合 = 変換失敗
    return `(${escapedName} IS NOT NULL AND TRY_CAST(${escapedName} AS ${duckdbType}) IS NULL)`;
  });

  // いずれかの列で変換失敗した行を抽出
  const whereClause = conditions.join(' OR ');

  // Row #列を別名で取得（arrowToOperationResultでの自動削除を回避）
  return `SELECT ${escapeColumnName(ROW_ID_COLUMN_NAME)} AS source_row_id FROM ${escapeColumnName(sourceTableName)} WHERE ${whereClause}`;
}

/**
 * 変換プレビュー用のサマリSQL を生成
 *
 * 各列の変換成功/失敗件数を集計する
 *
 * @param sourceTableName - 元テーブル名
 * @param conversions - 変換設定
 * @returns サマリSQL
 */
export function generatePreviewSummarySQL(
  sourceTableName: string,
  conversions: ColumnConversion[]
): string {
  const activeConversions = conversions.filter(
    c => c.enabled && c.fromType !== c.toType
  );

  if (activeConversions.length === 0) {
    return '';
  }

  const countClauses = activeConversions.map(conv => {
    const duckdbType = toDuckDBType(conv.toType);
    const escapedName = escapeColumnName(conv.columnName);
    return `
      COUNT(*) FILTER (WHERE TRY_CAST(${escapedName} AS ${duckdbType}) IS NOT NULL OR ${escapedName} IS NULL) AS "${conv.columnName}_ok",
      COUNT(*) FILTER (WHERE ${escapedName} IS NOT NULL AND TRY_CAST(${escapedName} AS ${duckdbType}) IS NULL) AS "${conv.columnName}_fail"
    `.trim();
  });

  return `
    SELECT ${countClauses.join(',\n       ')}
    FROM ${escapeColumnName(sourceTableName)}
  `.trim();
}
